import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LeadService } from '../shared/service/leads.service';
import { ToastrService } from 'ngx-toastr';
import { PickListService } from '../../../core/shared/services/picklist.service';
import { PKL_GRP } from '../../../core/shared/models/picklist.model';
import { take } from 'rxjs/operators';
 import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  ShadingType
} from 'docx';
import { saveAs } from 'file-saver';
import { Setup } from '../../setup/shared/service/setup';
import { BackButton } from "../../shared/buttons/back-button";
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RichTextEditor } from '../../credit/shared/components/rich-text-editor/rich-text-editor';
import {
  hasRichTextContent,
  isRichTextHtml,
  richTextForSave,
  sanitizeRichText,
} from '../../credit/shared/utils/rich-text.util';

@Component({
  selector: 'app-add-terms',
  standalone: true,
  imports: [CommonModule, FormsModule, BackButton, TranslatePipe, RichTextEditor],
  templateUrl: './add-terms.html',
})
export class AddTerms implements OnInit {
isViewMode = false;
  /* ================= STATE ================= */
  leadSeq: number | null = null;
  isLoading = false;
  instituteName = '';

  /* ================= DROPDOWN ================= */
  sectionOptions: { label: string; value: number }[] = [];
cities: { label: string; value: any }[] = [];
provinces: { label: string; value: any }[] = [];
  /* ================= FORM DATA ================= */
  sections: any[] = [];

  constructor(
    private route: ActivatedRoute,
    private leadService: LeadService,
    private toastr: ToastrService,
    private pickListService: PickListService,
    private setup: Setup,
      private translate: TranslateService,
      private router: Router

  ) {}

  /* ================= INIT ================= */

ngOnInit(): void {
  this.route.paramMap.subscribe((params) => {
    const id = params.get('id');
    this.leadSeq = id ? +id : null;  

    this.loadPicklist();              
  });

  this.route.queryParamMap.subscribe((q) => {
    this.isViewMode = q.get('mode') === 'view';
  });
}

  /* ================= PICKLIST ================= */

  loadPicklist(): void {
    this.pickListService.loadCodes([PKL_GRP.TERMS_SECTION]);

    this.pickListService.picklists$
      .pipe(take(1))
      .subscribe(() => {

        const options = this.pickListService.getOptionsSync(PKL_GRP.TERMS_SECTION);

        this.sectionOptions = options.map(o => ({
          label: o.label,
          value: o.value
        }));

        // 🔥 AFTER PICKLIST LOAD → LOAD DATA
        if (this.leadSeq) {
          this.loadExistingTerms(this.leadSeq);
          this.loadLeadDetails(this.leadSeq);
        }
      });
  }

  loadLeadDetails(leadSeq: number): void {
    this.leadService.getLeadById(leadSeq).subscribe({
      next: (res: any) => {
        const lead = res?.data ?? res;
        this.instituteName = lead?.clntNm || lead?.nmOfInstitution || '';
      },
      error: () => {
        this.instituteName = '';
      }
    });
  }

  /* ================= LOAD TERMS ================= */
loadCities(): void {
  this.setup.getCity().subscribe({
    next: (res: any[]) => {
      this.cities = (res || []).map(c => ({
        label: c.cityNm,
        value: c.citySeq
      }));
    }
  });
}
  loadExistingTerms(leadSeq: number): void {
    this.isLoading = true;

    this.leadService.getLeadTermList(leadSeq).subscribe({
      next: (res: any) => {

        const list = Array.isArray(res) ? res : [];

        this.sections = list.map((t: any) => ({
          trmSheetSeq: t.trmSheetSeq,
          code: t.trmCdKey,
          description: t.trmNote
        }));

        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }
hasValidSections(): boolean {
return this.sections.some(s => s.trmSheetSeq && s.code);}
  /* ================= ADD / DELETE ================= */
submitLead(): void {
  // Same checks as Save (lead id, section codes, descriptions), so a section
  // with an empty description blocks Submit too.
  if (!this.validate() || !this.leadSeq) {
    return;
  }

  if (!this.hasValidSections()) {
    this.toastr.warning(this.translate.instant('TERM_SHEET.ADD_AT_LEAST_ONE_VALID_SECTION'));
    return;
  }

  this.isLoading = true;

  this.leadService.submitLead(this.leadSeq, 2).subscribe({
    next: (res: any) => {
      console.log('Submit lead response:', res);

      // ErrorInterceptor already shows a toast for top-level error/message
      // shapes (and flags __hasError) — don't toast the same message again.
      if (res?.__hasError === true) {
        this.isLoading = false;
        return;
      }

      const nestedErrorMessage = res?.data?.error || res?.data?.message || '';

      if (nestedErrorMessage) {
        this.toastr.error(nestedErrorMessage);
        this.isLoading = false;
        return;
      }

      if (res?.success === false) {
        this.toastr.error(res?.message || this.translate.instant('TERM_SHEET.LEAD_SUBMISSION_FAILED'));
        this.isLoading = false;
        return;
      }

      this.toastr.success(this.translate.instant('TERM_SHEET.LEAD_SUBMITTED_SUCCESSFULLY'));
      this.isLoading = false;

      this.router.navigate(['/leads']);
    },

    error: (err: any) => {
      // ErrorInterceptor already shows a toast for HTTP error responses —
      // avoid displaying the same message a second time here.
      console.error('Submit lead HTTP error:', err);
      this.isLoading = false;
    },
  });
}
addSection(): void {
  // New sections go below the ones already added.
  this.sections.push({
    trmSheetSeq: null,
    code: null,
    description: ''
  });
}

  deleteSection(index: number): void {
    const section = this.sections[index];

    // Not yet saved to backend — just remove locally
    if (!section?.trmSheetSeq) {
      this.sections.splice(index, 1);
      return;
    }

    // Already persisted — mark deleted on backend so it doesn't
    // reappear on the next loadExistingTerms() refresh
    this.isLoading = true;

    this.leadService.saveTermSheet({
      trmSheetSeq: section.trmSheetSeq,
      trmCdKey: Number(section.code),
      trmDesc: section.description || '',
      trmNote: section.description || '',
      delFlg: true,
      leadSeq: this.leadSeq
    }).subscribe({
      next: () => {
        this.sections.splice(index, 1);
        this.isLoading = false;
        this.toastr.success(this.translate.instant('TERM_SHEET.SECTION_DELETED'));
      },
      error: () => {
        this.isLoading = false;
        this.toastr.error(this.translate.instant('TERM_SHEET.DELETE_FAILED'));
      }
    });
  }

  /* ================= VALIDATION ================= */

  /** Set on the first save attempt, so empty descriptions are only flagged after that. */
  descriptionChecked = false;

  isDescriptionMissing(section: any): boolean {
    // The editor gives HTML, so an empty-looking value can still be '<p></p>'.
    return !hasRichTextContent(section?.description);
  }

  validate(): boolean {

    if (!this.leadSeq) {
      this.toastr.error(this.translate.instant('TERM_SHEET.LEAD_ID_MISSING'));
      return false;
    }

    if (!this.sections.length) {
      this.toastr.warning(this.translate.instant('TERM_SHEET.ADD_AT_LEAST_ONE_SECTION'));
      return false;
    }

    const invalid = this.sections.some(s => !s.code);
    if (invalid) {
      this.toastr.warning(this.translate.instant('TERM_SHEET.SELECT_SECTION_CODE'));
      return false;
    }

    this.descriptionChecked = true;
    if (this.sections.some(s => this.isDescriptionMissing(s))) {
      this.toastr.warning(this.translate.instant('TERM_SHEET.ENTER_DESCRIPTION'));
      return false;
    }

    const codes = this.sections.map(s => s.code);
    if (new Set(codes).size !== codes.length) {
      this.toastr.warning(this.translate.instant('TERM_SHEET.DUPLICATE_SECTION_CODES'));
      return false;
    }

    return true;
  }

  /* ================= SAVE ================= */

  async saveTerms(): Promise<void> {

    if (!this.validate()) return;
    if (!this.leadSeq) return;

    this.isLoading = true;

    try {
      await Promise.all(
        this.sections.map(s =>
          this.leadService.saveTermSheet({
            trmSheetSeq: s.trmSheetSeq || null, // ✅ update support
            trmCdKey: Number(s.code),
            trmDesc: richTextForSave(s.description),
            trmNote: richTextForSave(s.description),
            delFlg: false,
            leadSeq: this.leadSeq
          }).toPromise()
        )
      );

      this.toastr.success(this.translate.instant('TERM_SHEET.TERMS_SAVED_SUCCESSFULLY'));

      // 🔥 REFRESH DATA
      this.loadExistingTerms(this.leadSeq);

    } catch {
      this.toastr.error(this.translate.instant('TERM_SHEET.SAVE_FAILED'));
    } finally {
      this.isLoading = false;
    }
  }



/* ================= DESCRIPTION → WORD ================= */

/**
 * Turns the editor's HTML into Word paragraphs: <p> → paragraph, list items →
 * "• " / "1. " lines, and bold / italic / underline / colour / highlight /
 * font / size → TextRun formatting. Older plain-text descriptions are split by line.
 */
private descriptionParagraphs(value: string | null | undefined): Paragraph[] {
  if (!isRichTextHtml(value)) {
    const lines = String(value ?? '').split(/\r?\n/);
    return lines.map(line => new Paragraph({ children: [new TextRun(line)] }));
  }

  const container = document.createElement('div');
  container.innerHTML = sanitizeRichText(value as string);

  const output: Paragraph[] = [];
  container.childNodes.forEach(node => {
    const tag = node instanceof HTMLElement ? node.tagName.toLowerCase() : '';

    if (tag === 'ul' || tag === 'ol') {
      Array.from((node as HTMLElement).children).forEach((item, index) => {
        const prefix = tag === 'ol' ? `${index + 1}. ` : '• ';
        output.push(new Paragraph({
          indent: { left: 320, hanging: 200 },
          children: [new TextRun(prefix), ...this.descriptionRuns(item, {})]
        }));
      });
      return;
    }

    const runs = this.descriptionRuns(node, {});
    if (runs.length) {
      output.push(new Paragraph({ children: runs }));
    }
  });

  // A table cell must hold at least one paragraph.
  return output.length ? output : [new Paragraph({ children: [] })];
}

private descriptionRuns(node: Node, marks: Record<string, any>): TextRun[] {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent ?? '';
    return text ? [new TextRun({
      text,
      bold: marks['bold'],
      italics: marks['italics'],
      underline: marks['underline'] ? {} : undefined,
      strike: marks['strike'],
      color: marks['color'],
      font: marks['font'],
      // docx sizes are half-points; the editor's px sizes are close enough to pt.
      size: marks['sizePx'] ? Math.round(marks['sizePx'] * 1.5) : undefined,
      shading: marks['highlight']
        ? { type: ShadingType.CLEAR, color: 'auto', fill: marks['highlight'] }
        : undefined,
    })] : [];
  }

  if (!(node instanceof HTMLElement)) {
    return [];
  }

  const tag = node.tagName.toLowerCase();
  if (tag === 'br') {
    return [new TextRun({ text: '', break: 1 })];
  }

  const next = { ...marks };
  if (tag === 'strong' || tag === 'b') next['bold'] = true;
  if (tag === 'em' || tag === 'i') next['italics'] = true;
  if (tag === 'u') next['underline'] = true;
  if (tag === 's') next['strike'] = true;

  const color = this.cssColorToHex(node.style.color);
  if (color) next['color'] = color;
  const highlight = this.cssColorToHex(node.style.backgroundColor || node.getAttribute('data-color') || '');
  if (highlight) next['highlight'] = highlight;
  const font = node.style.fontFamily?.split(',')[0]?.replace(/["']/g, '').trim();
  if (font) next['font'] = font;
  const sizePx = parseFloat(node.style.fontSize);
  if (Number.isFinite(sizePx) && sizePx > 0) next['sizePx'] = sizePx;

  const runs: TextRun[] = [];
  node.childNodes.forEach((child, index) => {
    // A list item's text sits in one or more <p>; keep them on separate lines.
    if (index > 0 && child instanceof HTMLElement && child.tagName.toLowerCase() === 'p') {
      runs.push(new TextRun({ text: '', break: 1 }));
    }
    runs.push(...this.descriptionRuns(child, next));
  });
  return runs;
}

/** '#ff0000' / 'rgb(255, 0, 0)' → 'FF0000'; anything else → null. */
private cssColorToHex(value: string): string | null {
  const color = (value || '').trim();
  const hex = color.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const digits = hex[1].length === 3 ? hex[1].split('').map(c => c + c).join('') : hex[1];
    return digits.toUpperCase();
  }
  const rgb = color.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgb) {
    return rgb.slice(1, 4).map(n => Number(n).toString(16).padStart(2, '0')).join('').toUpperCase();
  }
  return null;
}

downloadTerms(): void {

  const doc = new Document({
    styles: {
      default: {
        document: {
          run: {
            font: "Calibri",
            size: 21 // 10.5 * 2 (docx uses half-points)
          }
        }
      }
    },
    sections: [
      {
        children: [

          // MAIN HEADER (CENTER)
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { after: 100 },
            children: [
              new TextRun({
                text: 'Indicative Term Sheet',
                bold: true,
                size: 24 // bigger for title
              })
            ]
          }),

          // SUB HEADER (CENTER)
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { after: 200 },
            children: [
              new TextRun({
                text: 'Standard Term Loan Facility',
                bold: true,
                size: 22
              })
            ]
          }),
           new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { after: 200 },
            children: [
              new TextRun({
                text: this.instituteName || 'N/A',
                bold: true,
                size: 22
              })
            ]
          }),

          // DESCRIPTION
          new Paragraph({
            spacing: { after: 200 },
            children: [
              new TextRun({
                text: 'Terms and Conditions mentioned below are subjective in nature and should not be considered as firm offer or binding. The terms and Conditions are subject to satisfactory due diligence and approvals.'
              })
            ]
          }),

          // TABLE
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows: this.sections.map(sec =>
              new TableRow({
                children: [

                  // LEFT COLUMN (LABEL)
                  new TableCell({
                    width: { size: 40, type: WidthType.PERCENTAGE },
                    children: [
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: this.getLabel(sec.code),
                            bold: true
                          })
                        ]
                      })
                    ]
                  }),

                  // RIGHT COLUMN VALUE
                  new TableCell({
                    width: { size: 60, type: WidthType.PERCENTAGE },
                    children: this.descriptionParagraphs(sec.description)
                  })
                ]
              })
            )
          }),

          // SPACE
          new Paragraph({ text: '', spacing: { after: 200 } }),

          // FOOTER TEXT
          new Paragraph({
            spacing: { after: 100 },
            children: [
              new TextRun({
                text: 'Accepted For and On Behalf Of:',
                bold: true
              })
            ]
          }),

          // LINE
          new Paragraph({
            children: [
              new TextRun('____________________________')
            ]
          }),

          // CEO
          new Paragraph({
            children: [
              new TextRun('CEO,')
            ]
          }),

          // COMPANY
          new Paragraph({
            children: [
              new TextRun('Pakistan Microfinance Investment Company Limited (PMIC)')
            ]
          })

        ]
      }
    ]
  });

  Packer.toBlob(doc).then(blob => {
    saveAs(blob, 'Term-Sheet.docx');
  });
}

getLabel(code: number): string {
  const found = this.sectionOptions.find(s => s.value === code);
  return found ? found.label : 'Unknown';
}
}